import type { SeverityLevel, StatusRole } from '@lighthouse/design'
import { NOT_MEASURED, agoLabel, errorKind, errorStatus, errorSummary, exactNumber } from '../../utils/format'
import { isApiError } from '../../services/apiError'
import { isLinkableSubjectKey } from '../../utils/deepLink'
import { queryView, requestErrorText } from '../overview/overviewFormat'
import type { QueryLike } from '../overview/overviewFormat'
import {
  SIGNAL_HANDLING_FILTERS, SIGNAL_LIST_STATES, SIGNAL_REASONS, SIGNAL_SEVERITIES, SIGNAL_SUBJECT_KINDS,
} from '../../types/signals'
import type {
  Signal, SignalActivity, SignalHandlingFilter, SignalListQuery, SignalListState, SignalReason, SignalRule,
  SignalSeverity, SignalSubjectKind, SignalSummary,
} from '../../types/signals'

/**
 * Every rule the Signal Inbox, the Overview's Issues and Critical tiles, the rail badge and the Health page's
 * detector row apply to the signal store's payloads before drawing them.
 *
 * Pure and vitest-safe on purpose: types, utils/format, services/apiError and the Overview's own pure helpers.
 * Nothing here imports services/api.ts (MSAL reads `window` at load) or a hook.
 *
 * THE RULE every function here keeps (build plan rule 1): an unobserved thing is never green. The store says how
 * much it can vouch for (`coverage`), and only `Complete` lets a zero mean "none of these conditions is present".
 * Anything less is a count with a caveat, or a dash — never a green 0. The detector not reporting, the store not
 * being provisioned, and no detector ever having run are three different non-answers, and each is said as itself.
 *
 * Signals are raised and resolved by Automaton's detector alone. A person may HANDLE one — acknowledge, assign,
 * release (D6b) — and that is worded in signalHandlingFormat.ts, never here: handling changes who has a signal, never
 * whether it is open, so no count, tone or state below reads it except to say how many are acknowledged. The one
 * ending a person causes is Data Comb's own acknowledgement, which is where that signal was silenced, and is named
 * as such.
 */

// ---------------------------------------------------------------------------
// Fixed copy
// ---------------------------------------------------------------------------

/** The migration that creates the store. Named wherever its absence is the reason. */
export const SIGNALS_MIGRATION = 'AddOperationalSignals'

/** The migration that creates the handling table (Etl.OperationalSignalHandling). */
export const HANDLING_MIGRATION = 'AddSignalHandlingAndRunCancellation'

/** The stated limit, always shown on the Inbox: the store has no incident layer (doc 05). The catalogue's own words. */
export const NOT_CORRELATED = 'Signals are not correlated: one failure can raise several.'

/**
 * The other stated limit: handling a signal never ends it (D6b). People acknowledge and assign; only the detector
 * resolves — with the one exception it names, Data Comb's own acknowledgement.
 */
export const DETECTOR_ONLY =
  'Raised and cleared by the detector only. An administrator can acknowledge a signal or hand it to someone; that '
  + 'never closes or hides it — it stays open and counted until the detector sees the condition clear. Nobody '
  + 'resolves or snoozes a signal here, except that acknowledging a Data Comb finding on the Data Comb page resolves '
  + "that finding's signal (Suppressed at source) and keeps that finding from raising one again."

// ---------------------------------------------------------------------------
// Small readers
// ---------------------------------------------------------------------------

function plural(n: number, one: string, many = `${one}s`): string {
  return `${exactNumber(n)} ${n === 1 ? one : many}`
}

function has<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value)
}

/** A rule's name as the reader should see it: its published title, else its id (no detector has published it). */
export function ruleName(rule: Pick<SignalRule, 'ruleId' | 'title'>): string {
  return rule.title?.trim() || rule.ruleId
}

/** A signal's rule title, else its rule id. */
export function signalTitle(s: Pick<Signal, 'ruleId' | 'ruleTitle'>): string {
  return s.ruleTitle?.trim() || s.ruleId
}

/** The rules a tile speaks for: every served rule not withdrawn, or the Critical ones. */
export function scopedRules(summary: SignalSummary, scope: TileScope): SignalRule[] {
  const live = summary.rules.filter((r) => r.evaluation.outcome !== 'Retired')
  return scope === 'critical' ? live.filter((r) => r.severity === 'Critical') : live
}

/**
 * Whether a rule's last evaluation leaves it unable to vouch for its subjects — the server's own "Incomplete" test
 * (OperationalSignalRules.Coverage): no row, a stale row, or an outcome other than Evaluated or Partial.
 */
export function isRuleIncomplete(rule: SignalRule): boolean {
  const outcome = rule.evaluation.outcome
  return rule.evaluation.isStale || (outcome !== 'Evaluated' && outcome !== 'Partial')
}

/** Why a rule is incomplete, in words: its reason when it gave one, else what its outcome says. */
export function incompleteReason(rule: SignalRule): string {
  const e = rule.evaluation
  if (e.outcome !== 'Evaluated' && e.outcome !== 'Partial') {
    if (e.reason) return reasonText(e.reason)
    switch (e.outcome) {
      case 'NotReporting': return 'No detector has evaluated it'
      case 'InputUnavailable': return "Its input could not be read"
      case 'Failed': return 'Its evaluation failed'
      case 'Retired': return 'It has been withdrawn from the catalogue'
      default: return `Its last outcome was ${e.outcome}`
    }
  }
  return e.lastEvaluatedAt ? `Not evaluated since ${agoLabel(e.lastEvaluatedAt)}` : 'Not evaluated recently'
}

function subjectsNotObserved(rules: readonly SignalRule[]): number {
  return rules.reduce((sum, r) => sum + (r.evaluation.subjectsUnobserved ?? 0), 0)
}

// ---------------------------------------------------------------------------
// Reasons, resolutions, severities, kinds
// ---------------------------------------------------------------------------

/**
 * A fixed sentence for every SignalReason. No trailing full stop: callers use these as sentences and as clauses.
 * The server serves names only, so this table is the only place a reason is worded.
 */
const REASON_TEXT: Record<SignalReason, string> = {
  InputUnreadable: "The rule's input could not be read",
  BrokerUnavailable: 'The message broker could not be reached',
  QueueUnavailable: 'The queue could not be inspected',
  ProjectionStale: 'The host reporting it has stopped publishing',
  SourceIdle: 'Nothing has contacted the source recently',
  NotYetContacted: 'The source has not been contacted yet',
  ObserverRestarted: 'The observing host restarted and has not observed it since',
  CombNeverCompleted: 'Data Comb has never completed a pass',
  CombTableSkipped: 'The newest complete Data Comb pass skipped the table',
  CombSweepDisabled: 'That Data Comb check is switched off',
  CombColumnNotProfiled: 'The newest complete Data Comb pass did not profile the column',
  CombRuleUnproven: 'The newest complete Data Comb pass cannot show whether it recovered',
  CombLatestPassFailed: 'The newest Data Comb pass did not complete, so an older complete pass was read',
  CombPassInProgress: 'A Data Comb pass is running, so the last complete pass was read',
  RuleTimedOut: "The rule's evaluation timed out",
  RuleFailed: "The rule's evaluation failed",
  NotClassified: 'The detector could not classify it on its last pass',
  DetectorNotReporting: 'No detector has reported recently, so nothing has checked it since',
  RuleNotReporting: 'Its rule has not been evaluated recently, so nothing has checked it since',
}

/** The sentence for a reason name; a name this build does not know renders as itself, never as a guess. */
export function reasonText(reason: string): string {
  return has(SIGNAL_REASONS, reason) ? REASON_TEXT[reason] : reason
}

/**
 * How a resolved signal ended. Only `ConditionCleared` means the subject was read and the condition is gone; every
 * other ending is NOT evidence that anything recovered, and none of them may be drawn as good.
 */
export function resolutionText(resolution: string | null, kind?: string): string {
  switch (resolution) {
    case 'ConditionCleared': return 'Cleared'
    case 'NoLongerReported': return 'No longer reported'
    case 'SuppressedAtSource': return kind === undefined || kind === 'DataCombFinding' ? 'Acknowledged in Data Comb' : 'Suppressed at its source'
    case 'SubjectRemoved': return 'No longer tracked'
    case 'OutOfScope': return 'Out of scope — left the resume window'
    case null: return 'No longer open'
    default: return resolution
  }
}

/** The design system's severity scale, which is deliberately not the status scale. */
export function severityLevel(severity: string): SeverityLevel {
  switch (severity) {
    case 'Critical': return 'critical'
    case 'Warning': return 'high'
    default: return 'low'
  }
}

export function severityLabel(severity: string): string {
  return has(SIGNAL_SEVERITIES, severity) ? severity : severity || NOT_MEASURED
}

const KIND_LABEL: Record<SignalSubjectKind, string> = {
  EtlHalt: 'ETL halt',
  EtlRun: 'ETL run',
  DataCombFinding: 'Data Comb finding',
  SourceConnection: 'Source connection',
  DeadLetterQueue: 'Dead-letter queue',
}

export function subjectKindLabel(kind: string): string {
  return has(SIGNAL_SUBJECT_KINDS, kind) ? KIND_LABEL[kind] : kind
}

// ---------------------------------------------------------------------------
// The detector: never run, not provisioned, not reporting
// ---------------------------------------------------------------------------

export type UnmeasuredKind = 'unprovisioned' | 'never-run' | 'stale' | 'unavailable'

export interface Unmeasured {
  kind: UnmeasuredKind
  /** The tile's sublabel. */
  tile: string
  /** A full sentence, for the badge's tooltip and the empty states. */
  sentence: string
}

/**
 * Why the store cannot vouch for anything at all, or null when it can say something.
 *
 * Checked in this order because each hides the next: an unprovisioned store has no rows to be stale, and a
 * detector that never ran has no evaluation to be old. `coverage: 'Unavailable'` is exactly the last two, and is
 * checked after them only so a payload that says Unavailable for a reason this build does not know is still a dash.
 */
export function unmeasuredReason(summary: SignalSummary): Unmeasured | null {
  if (!summary.storeProvisioned) {
    return {
      kind: 'unprovisioned',
      tile: `signal store not provisioned here (migration ${SIGNALS_MIGRATION})`,
      sentence: `The signal store is not provisioned in this database: migration ${SIGNALS_MIGRATION} has not been applied.`,
    }
  }
  if (summary.detector.hasNeverRun) {
    return {
      kind: 'never-run',
      tile: 'signal detector has not run yet',
      sentence: 'The signal detector has not run against this database yet, so no condition has been checked.',
    }
  }
  if (summary.detector.isStale) {
    const at = summary.detector.lastEvaluatedAt
    return {
      kind: 'stale',
      tile: at ? `detector not reporting since ${agoLabel(at)}` : 'detector not reporting',
      sentence: at
        ? `The signal detector has not reported since ${agoLabel(at)}, so nothing has been checked since.`
        : 'The signal detector is not reporting, so nothing is being checked.',
    }
  }
  if (summary.coverage === 'Unavailable') {
    return {
      kind: 'unavailable',
      tile: 'signal coverage unavailable',
      sentence: 'The signal store cannot vouch for any rule right now.',
    }
  }
  return null
}

/** "Detector last evaluated 1m ago on HOST", for the Inbox header. */
export function detectorHeaderText(summary: SignalSummary): string {
  const { lastEvaluatedAt: at, evaluatedByHost: host } = summary.detector
  if (!at) return 'The detector has not evaluated any rule'
  return `Detector last evaluated ${agoLabel(at)}${host ? ` on ${host}` : ''}`
}

export interface DetectorBanner {
  kind: Exclude<UnmeasuredKind, 'unavailable'>
  title: string
  message: string
}

/** The Inbox's banner when the detector cannot be believed; null when it is reporting. */
export function detectorBanner(summary: SignalSummary): DetectorBanner | null {
  const u = unmeasuredReason(summary)
  if (!u || u.kind === 'unavailable') return null
  if (u.kind === 'unprovisioned') {
    return {
      kind: u.kind,
      title: 'Signal store not provisioned here',
      message: `The signal tables do not exist in this database: migration ${SIGNALS_MIGRATION} has not been applied. `
        + 'Nothing is raised or listed until it is, and this is not an empty inbox.',
    }
  }
  if (u.kind === 'never-run') {
    return {
      kind: u.kind,
      title: 'The signal detector has not run yet',
      message: 'No detector has evaluated any rule against this database. Until one does, nothing is raised, and an '
        + 'empty inbox would mean nothing.',
    }
  }
  const at = summary.detector.lastEvaluatedAt
  const host = summary.detector.evaluatedByHost
  return {
    kind: 'stale',
    title: at ? `Detector not reporting since ${agoLabel(at)}` : 'Detector not reporting',
    message: `No pass has landed within its ${plural(summary.detector.staleWindowMinutes, 'minute')} window`
      + `${host ? ` (last from ${host})` : ''}, so open signals are shown as unconfirmed: nothing has checked them since. `
      + 'A condition that began after that pass is not here at all.',
  }
}

/** One line of the Health page's "Signal detector" row. `loading` draws a skeleton. */
export type DetectorLine =
  | { kind: 'loading' }
  | { kind: 'line'; status: StatusRole | null; label: string; detail: string }

/**
 * The Health page's detector row: whether the thing that raises signals is running.
 *
 * Only a fresh evaluation is green, and only a stale one is red — a detector that has stopped is a fault in the
 * monitoring itself. Unprovisioned, never run and a failed request are dashes: none of them is an answer about the
 * detector's health.
 */
export function detectorLine(q: QueryLike<SignalSummary>): DetectorLine {
  const view = queryView(q)
  if (view === 'loading') return { kind: 'loading' }
  if (view === 'error') {
    return { kind: 'line', status: null, label: 'no answer', detail: requestErrorText('the signal summary', q.error) }
  }
  const s = q.data!
  const host = s.detector.evaluatedByHost
  const at = s.detector.lastEvaluatedAt
  if (!s.storeProvisioned) {
    return {
      kind: 'line', status: null, label: 'store not provisioned here',
      detail: `The signal tables do not exist in this database (migration ${SIGNALS_MIGRATION}).`,
    }
  }
  if (s.detector.hasNeverRun) {
    return { kind: 'line', status: null, label: 'has not run', detail: 'No detector has evaluated any rule against this database.' }
  }
  if (s.detector.isStale) {
    return {
      kind: 'line', status: 'critical', label: 'Not reporting',
      detail: at ? `last evaluated ${agoLabel(at)}${host ? ` on ${host}` : ''}` : 'no evaluation recorded',
    }
  }
  if (!at) {
    return { kind: 'line', status: null, label: 'unavailable', detail: 'The summary names no evaluation.' }
  }
  const caveat = s.coverage === 'Complete' ? '' : ` · coverage ${s.coverage.toLowerCase()}`
  return {
    kind: 'line', status: 'good', label: 'Reporting',
    detail: `evaluated ${agoLabel(at)}${host ? ` on ${host}` : ''}${caveat}`,
  }
}

// ---------------------------------------------------------------------------
// The Overview's Issues and Critical tiles
// ---------------------------------------------------------------------------

export type TileScope = 'all' | 'critical'

export interface SignalTile {
  value: string
  /** Absent whenever nothing was measured — and never `good` unless coverage is Complete. */
  status: StatusRole | undefined
  sublabel: string
}

function dash(sublabel: string): SignalTile {
  return { value: NOT_MEASURED, status: undefined, sublabel }
}

/**
 * The Issues tile (`'all'`) or the Critical tile (`'critical'`), from the summary and the coverage that matches it.
 *
 * - loading, a failed request, an unprovisioned store, a detector that never ran or has stopped: a dash each, with
 *   its own words. None prints a number: Unavailable coverage serves null counts, and a count served beside it
 *   anyway is still not believed.
 * - Incomplete: the count only when it is above zero (a problem that WAS observed), never a good tone; a zero is a
 *   dash, because a rule that could not look proves nothing. The sublabel names the first rule that could not look.
 * - Partial: the count, never a good tone, with what was not confirmed.
 * - Complete: the count, and only here may a zero be green — unless a rule reports subjects IN its condition that it
 *   deliberately does not signal (`excludedCount`: the stale rule's runs older than the resume window). Those are
 *   named in the sublabel, and a zero beside them is not green: the Overview's Critical Attention counts every stale
 *   run, and the two must not tell the reader opposite things on one page.
 */
export function tileView(q: QueryLike<SignalSummary>, scope: TileScope): SignalTile {
  const view = queryView(q)
  if (view === 'error') return dash(requestErrorText('the signal summary', q.error))
  if (view === 'loading') return dash('loading')

  const s = q.data!
  const unmeasured = unmeasuredReason(s)
  if (unmeasured) return dash(unmeasured.tile)

  const coverage = scope === 'critical' ? s.criticalCoverage : s.coverage
  const count = scope === 'critical' ? s.openCriticalCount : s.openCount
  const unconfirmed = (scope === 'critical' ? s.openCriticalUnconfirmedCount : s.openUnconfirmedCount) ?? 0
  if (coverage === 'Unavailable' || count == null) return dash('signal coverage unavailable')

  const rules = scopedRules(s, scope)
  const alarm: StatusRole = scope === 'critical' ? 'critical' : 'warning'
  const when = s.detector.lastEvaluatedAt ? agoLabel(s.detector.lastEvaluatedAt) : NOT_MEASURED
  const ruleWord = scope === 'critical' ? 'critical rule' : 'rule'

  const excluded = excludedNote(rules)
  // A PART of the count, said beside it: the value stays the open count, never "open minus acknowledged".
  const acknowledged = scope === 'critical' ? s.openCriticalAcknowledgedCount : s.openAcknowledgedCount
  const counted = (sublabel: string) =>
    acknowledged != null ? `${sublabel} · ${exactNumber(acknowledged)} acknowledged` : sublabel

  switch (coverage) {
    case 'Complete':
      if (count === 0) {
        return excluded
          ? { value: '0', status: undefined, sublabel: `0 open · ${excluded}` }
          : { value: '0', status: 'good', sublabel: `all ${plural(rules.length, ruleWord)} evaluated ${when}` }
      }
      return {
        value: exactNumber(count),
        status: alarm,
        sublabel: counted(`open · ${excluded ?? `all ${plural(rules.length, ruleWord)} evaluated ${when}`}`),
      }

    case 'Partial': {
      const parts: string[] = []
      if (unconfirmed > 0) parts.push(`${exactNumber(unconfirmed)} unconfirmed`)
      const unseen = subjectsNotObserved(rules)
      if (unseen > 0) parts.push(`${plural(unseen, 'subject')} not observed`)
      if (parts.length === 0) {
        const partial = rules.find((r) => r.evaluation.outcome === 'Partial')
        parts.push(partial?.evaluation.reason ? reasonText(partial.evaluation.reason) : 'partly evaluated')
      }
      if (excluded) parts.push(excluded)
      const sublabel = parts.join(' · ')
      return count > 0
        ? { value: exactNumber(count), status: alarm, sublabel: counted(sublabel) }
        : { value: exactNumber(count), status: undefined, sublabel }
    }

    case 'Incomplete': {
      const sublabel = incompleteSublabel(rules)
      return count > 0 ? { value: exactNumber(count), status: alarm, sublabel: counted(sublabel) } : dash(sublabel)
    }

    default:
      // A coverage value this build does not know: nothing it could mean is safe to colour.
      return dash(`coverage ${String(coverage)} is not one this build knows`)
  }
}

/**
 * "3 older stale runs not signalled": the subjects in a rule's condition it deliberately does not signal, across the
 * tile's rules — or null when there are none. Only the stale rule reports any in this build.
 */
function excludedNote(rules: readonly SignalRule[]): string | null {
  const stale = rules.filter((r) => r.subjectKind === 'EtlRun').reduce((n, r) => n + Math.max(0, r.excludedCount ?? 0), 0)
  const other = rules.filter((r) => r.subjectKind !== 'EtlRun').reduce((n, r) => n + Math.max(0, r.excludedCount ?? 0), 0)
  const parts: string[] = []
  if (stale > 0) parts.push(`${plural(stale, 'older stale run')} not signalled`)
  if (other > 0) parts.push(`${plural(other, 'subject')} not signalled`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** "Critical Data Comb finding: Data Comb has never completed a pass · 1 more rule not evaluated". */
function incompleteSublabel(rules: readonly SignalRule[]): string {
  const incomplete = rules.filter(isRuleIncomplete)
  if (incomplete.length === 0) return 'not every rule was evaluated'
  const first = `${ruleName(incomplete[0])}: ${incompleteReason(incomplete[0])}`
  const more = incomplete.length - 1
  return more > 0 ? `${first} · ${plural(more, 'more rule')} not evaluated` : first
}

// ---------------------------------------------------------------------------
// The rail badge
// ---------------------------------------------------------------------------

/**
 * The Signal Inbox rail badge: OBSERVED open critical signals, or why none can be counted.
 *
 * `openCriticalCount − openCriticalUnconfirmedCount`, because an unconfirmed signal is not known to be wrong NOW —
 * the detector could not look — and a badge means "this needs you" (useNavBadges.ts: an unmeasured count is never
 * shown as a problem). The non-answers come back as `unmeasured`, for a muted dash with its sentence; loading is
 * nothing at all, so the rail does not flicker on every mount.
 *
 * Quiet (`{}`) ONLY when the store can vouch for every critical rule — `criticalCoverage` Complete — and nothing
 * critical is open. This is the one rail row whose tone claims an emergency, so silence reads as all-clear: with no
 * observed count, an unconfirmed open critical, or critical coverage short of Complete, is a muted dash saying so,
 * the same non-answer the Critical tile draws as a dash.
 */
export function signalInboxBadge(q: { data?: SignalSummary; isLoading: boolean; isError: boolean }): {
  count?: number
  unmeasured?: string
} {
  if (q.isError) return { unmeasured: 'The signal summary could not be read, so critical signals are not counted.' }
  if (q.isLoading || !q.data) return {}
  const unmeasured = unmeasuredReason(q.data)
  if (unmeasured) return { unmeasured: unmeasured.sentence }
  const open = q.data.openCriticalCount
  if (open == null) return { unmeasured: 'Critical signals are not measured.' }
  const unconfirmed = q.data.openCriticalUnconfirmedCount ?? 0
  const observed = open - unconfirmed
  if (observed > 0) return { count: observed }
  if (unconfirmed > 0) {
    return {
      unmeasured: `${plural(unconfirmed, 'open critical signal')} could not be confirmed on the detector's last pass, `
        + 'so whether anything critical is wrong now is not known.',
    }
  }
  const coverage = q.data.criticalCoverage
  if (coverage !== 'Complete') {
    const why = coverage === 'Incomplete'
      ? incompleteSublabel(scopedRules(q.data, 'critical'))
      : coverage === 'Partial' ? 'not every subject was observed' : `coverage ${String(coverage)}`
    return {
      unmeasured: `Critical coverage is ${String(coverage).toLowerCase()} (${why}), so having no open critical signal `
        + 'is not an all-clear.',
    }
  }
  return {}
}

// ---------------------------------------------------------------------------
// One signal
// ---------------------------------------------------------------------------

/**
 * - `open` / `unconfirmed`: still open; unconfirmed means the last pass could not see the subject.
 * - `cleared`: the ONLY good ending — the subject was read and the condition is gone.
 * - `ended`: every other ending. Not a fault, and never a check mark: none of them proves recovery.
 */
export type SignalStateTone = 'open' | 'unconfirmed' | 'cleared' | 'ended'

export function signalState(s: Pick<Signal, 'isOpen' | 'observation' | 'unobservedSince' | 'resolution' | 'subjectKind'>): {
  text: string
  tone: SignalStateTone
} {
  if (s.isOpen) {
    if (s.observation === 'Observed') return { text: 'Open', tone: 'open' }
    return {
      text: s.unobservedSince ? `Open — unconfirmed since ${agoLabel(s.unobservedSince)}` : 'Open — unconfirmed',
      tone: 'unconfirmed',
    }
  }
  return {
    text: resolutionText(s.resolution, s.subjectKind),
    tone: s.resolution === 'ConditionCleared' ? 'cleared' : 'ended',
  }
}

/** The state as words: Open, "Open — unconfirmed since 5m ago", Cleared, No longer reported, and so on. */
export function signalStateText(s: Parameters<typeof signalState>[0]): string {
  return signalState(s).text
}

/** When the condition began, as its input recorded it — else when the detector first saw it. Said which. */
export function sinceText(s: Pick<Signal, 'conditionSince' | 'firstObservedAt'>): string {
  return s.conditionSince
    ? `condition began ${agoLabel(s.conditionSince)}`
    : `first observed ${agoLabel(s.firstObservedAt)}`
}

/**
 * Two different clocks, kept apart: `observed` is the INPUT's own timestamp for the evidence (null when the input
 * records none), `checked` is the detector's last pass that found the condition. A healthy detector re-checking an
 * old reading makes the second recent and the first old; showing only one would hide which.
 */
export function evidenceText(s: Pick<Signal, 'evidenceAsOf' | 'lastCheckedAt'>): { observed: string | null; checked: string } {
  return {
    observed: s.evidenceAsOf ? `last observed ${agoLabel(s.evidenceAsOf)}` : null,
    checked: `checked ${agoLabel(s.lastCheckedAt)}`,
  }
}

export interface SignalLink {
  to: string
  label: string
}

/**
 * Where a signal's subject lives, primary first. The store holds no copy of the subject — no halt detail, no
 * finding message — so the reader goes to the page that owns it.
 *
 * An id-based link only when the signal carries its subject's row id; without one, the list page that holds such
 * subjects. A source or a queue has no row id — its subject key IS its name (the connector id, the queue name) — so
 * it links to that one item on its list page (`/sources?connector=`, `/queues?queue=`), which marks it or says it is
 * not listed. An unknown subject kind gets no link at all rather than a guessed one.
 */
export function signalLinks(s: Pick<Signal, 'subjectKind' | 'subjectRefId' | 'subjectKey'>): SignalLink[] {
  const ref = s.subjectRefId?.trim() ? encodeURIComponent(s.subjectRefId.trim()) : null
  const key = isLinkableSubjectKey(s.subjectKey) ? encodeURIComponent(s.subjectKey.trim()) : null
  switch (s.subjectKind) {
    case 'EtlHalt':
      return ref
        ? [{ to: '/jobs', label: 'Open the Scheduler' }, { to: `/execution-history/${ref}`, label: 'Open the halted run' }]
        : [{ to: '/jobs', label: 'Open the Scheduler' }]
    case 'EtlRun':
      return ref
        ? [{ to: `/execution-history/${ref}`, label: 'Open the run' }]
        : [{ to: '/execution-history?status=stale', label: 'Open stale runs' }]
    case 'DataCombFinding':
      return ref
        ? [{ to: `/data-comb?finding=${ref}`, label: 'Open the finding' }]
        : [{ to: '/data-comb', label: 'Open the Data Comb' }]
    case 'SourceConnection':
      return key ? [{ to: `/sources?connector=${key}`, label: 'Open this source' }] : [{ to: '/sources', label: 'Open Sources' }]
    case 'DeadLetterQueue':
      return key ? [{ to: `/queues?queue=${key}`, label: 'Open this queue' }] : [{ to: '/queues', label: 'Open Queues' }]
    default:
      return []
  }
}

/** One transition of the drawer's timeline, in words. */
export function activityText(a: Pick<SignalActivity, 'kind' | 'reason'>, subjectKind?: string): string {
  switch (a.kind) {
    case 'Raised': return 'Raised'
    case 'Unobserved': return a.reason ? `Unconfirmed — ${reasonText(a.reason)}` : 'Unconfirmed'
    case 'Reobserved': return 'Observed again'
    case 'Resolved': return `Ended — ${resolutionText(a.reason, subjectKind)}`
    default: return a.reason ? `${a.kind} — ${a.reason}` : a.kind
  }
}

// ---------------------------------------------------------------------------
// The rule catalogue
// ---------------------------------------------------------------------------

/** How a rule's evaluation reads: `ok` and `partial` are plain text, `problem` is amber, `unmeasured` is a dash. */
export type RuleOutcomeTone = 'ok' | 'partial' | 'problem' | 'unmeasured'

export function ruleOutcome(rule: SignalRule): { label: string; tone: RuleOutcomeTone } {
  const e = rule.evaluation
  if (e.outcome === 'NotReporting') return { label: 'Not reporting', tone: 'unmeasured' }
  if (e.outcome === 'Retired') return { label: 'Retired', tone: 'unmeasured' }
  if (e.isStale) return { label: 'Stale', tone: 'unmeasured' }
  switch (e.outcome) {
    case 'Evaluated': return { label: 'Evaluated', tone: 'ok' }
    case 'Partial': return { label: 'Partly evaluated', tone: 'partial' }
    case 'InputUnavailable': return { label: 'Input unavailable', tone: 'problem' }
    case 'Failed': return { label: 'Failed', tone: 'problem' }
    default: return { label: String(e.outcome), tone: 'unmeasured' }
  }
}

/** The rule's last evaluation in one sentence: when, what came of it, why, and what it could not see. */
export function ruleEvaluationText(rule: SignalRule): string {
  const e = rule.evaluation
  if (e.outcome === 'NotReporting') return 'No detector has evaluated this rule.'
  if (e.outcome === 'Retired') return 'Withdrawn from the catalogue; its open signals ended as no longer tracked.'

  const when = e.lastEvaluatedAt ? agoLabel(e.lastEvaluatedAt) : 'at an unrecorded time'
  const lead = (() => {
    switch (e.outcome) {
      case 'Evaluated': return `Evaluated ${when}`
      case 'Partial': return `Partly evaluated ${when}`
      case 'InputUnavailable': return `Could not read its input ${when}; nothing was raised or cleared`
      case 'Failed': return `Evaluation failed ${when}; nothing was raised or cleared`
      default: return `${String(e.outcome)} ${when}`
    }
  })()
  const parts = [lead]
  if (e.reason) parts.push(reasonText(e.reason))
  if ((e.subjectsUnobserved ?? 0) > 0) parts.push(`${plural(e.subjectsUnobserved!, 'subject')} not observed`)
  if (e.isStale) parts.push('too old to believe: the detector has not evaluated it since')
  return `${parts.join(' — ')}.`
}

/** "input as of 3h ago" — the input's own clock, e.g. the Data Comb pass the rule read. */
export function inputAsOfText(rule: SignalRule): string {
  return rule.evaluation.inputAsOf ? `input as of ${agoLabel(rule.evaluation.inputAsOf)}` : 'input time not recorded'
}

/**
 * The stale rule's deliberately unsignalled runs: "4 older stale runs are not signalled — see Execution History".
 * Null for any other rule, and when there are none.
 */
export function excludedText(rule: SignalRule): string | null {
  if (rule.excludedCount == null || rule.excludedCount <= 0) return null
  if (rule.subjectKind === 'EtlRun') {
    return `${plural(rule.excludedCount, 'older stale run')} ${rule.excludedCount === 1 ? 'is' : 'are'} not signalled — see Execution History`
  }
  return `${plural(rule.excludedCount, 'subject')} in the condition ${rule.excludedCount === 1 ? 'is' : 'are'} not signalled`
}

// ---------------------------------------------------------------------------
// Empty lists
// ---------------------------------------------------------------------------

export interface EmptyView {
  title: string
  message: string | null
  /** Only a Complete store with nothing open may draw the check mark. */
  allClear: boolean
}

/**
 * What an empty list means. A filtered list proves nothing about the rest; an unfiltered one is an all-clear ONLY
 * under Complete coverage, and otherwise says what was not observed — without the check mark.
 */
export function emptyView(summary: SignalSummary, filtered: boolean): EmptyView {
  if (filtered) {
    return { title: 'No signal matches these filters', message: 'Other signals may exist: clear a filter to see them.', allClear: false }
  }
  const unmeasured = unmeasuredReason(summary)
  if (unmeasured) {
    return { title: 'No open signal on record', message: `${unmeasured.sentence} An empty list here is not a clean bill of health.`, allClear: false }
  }
  const rules = scopedRules(summary, 'all')
  const when = summary.detector.lastEvaluatedAt ? agoLabel(summary.detector.lastEvaluatedAt) : NOT_MEASURED
  if (summary.coverage === 'Complete') {
    return { title: `No open signals — all ${plural(rules.length, 'rule')} evaluated ${when}`, message: null, allClear: true }
  }
  return { title: 'No open signal on record — but not everything was observed', message: notObservedText(rules), allClear: false }
}

/** Every rule that could not look, and every subject that could not be seen — the reasons an empty list is not clear. */
export function notObservedText(rules: readonly SignalRule[]): string {
  const parts = rules.filter(isRuleIncomplete).map((r) => `${ruleName(r)}: ${incompleteReason(r)}.`)
  for (const r of rules) {
    if (isRuleIncomplete(r)) continue
    const unseen = r.evaluation.subjectsUnobserved ?? 0
    if (unseen > 0) parts.push(`${ruleName(r)}: ${plural(unseen, 'subject')} not observed.`)
    else if (r.evaluation.outcome === 'Partial') {
      parts.push(`${ruleName(r)}: ${r.evaluation.reason ? reasonText(r.evaluation.reason) : 'partly evaluated'}.`)
    }
  }
  return parts.length > 0 ? parts.join(' ') : 'The store could not vouch for every rule.'
}

/** What the store can vouch for right now, under the Inbox header. Never a reassurance it cannot back. */
export function coverageText(summary: SignalSummary): string {
  const unmeasured = unmeasuredReason(summary)
  if (unmeasured) return unmeasured.sentence
  const rules = scopedRules(summary, 'all')
  switch (summary.coverage) {
    case 'Complete':
      return `Coverage complete: all ${plural(rules.length, 'rule')} evaluated recently, and every subject was seen.`
    case 'Partial':
      return `Coverage partial — every rule evaluated, but not everything was seen. ${notObservedText(rules)}`
    case 'Incomplete':
      return `Coverage incomplete — not every rule could look. ${notObservedText(rules)}`
    default:
      return `Coverage ${String(summary.coverage)}: not a value this build knows, so nothing here is vouched for.`
  }
}

// ---------------------------------------------------------------------------
// Failed requests
// ---------------------------------------------------------------------------

export interface SignalErrorView {
  kind:
    | 'unauthorized' | 'forbidden' | 'network' | 'unprovisioned' | 'handling-unprovisioned' | 'unavailable'
    | 'server' | 'other'
  title: string
  message: string
}

const NOTHING_KNOWN = 'This says nothing about whether any signal is open.'

/**
 * A failed signal request, by status. Every branch ends by saying nothing is known: a red panel on an inbox reads
 * as "something is wrong", and all a failed request knows is that no answer came back.
 *
 * The list and the detail answer 503 ONLY when the store is not provisioned (the controller catches Msg 208 and
 * names the migration), and the list's `handling` filter answers 503 when the handling table is missing (naming
 * its own migration); a 503 that names neither came from something in front of the API.
 */
export function signalErrorView(error: unknown): SignalErrorView {
  const status = errorStatus(error)
  if (status === 503) {
    const said = isApiError(error) ? `${error.title ?? ''} ${error.detail ?? ''} ${error.message}` : ''
    if (said.includes(SIGNALS_MIGRATION)) {
      return {
        kind: 'unprovisioned',
        title: 'Signal store not provisioned here',
        message: `The signal tables do not exist in this database: migration ${SIGNALS_MIGRATION} has not been applied. ${NOTHING_KNOWN}`,
      }
    }
    if (said.includes(HANDLING_MIGRATION)) {
      return {
        kind: 'handling-unprovisioned',
        title: 'Signal handling not provisioned here',
        message: `The handling filter needs the handling table, which does not exist in this database: migration `
          + `${HANDLING_MIGRATION} has not been applied. Clear the handling filter to list signals. ${NOTHING_KNOWN}`,
      }
    }
    return {
      kind: 'unavailable',
      title: 'The API answered 503 (service unavailable)',
      message: `${errorSummary(error) ?? 'No reason was given.'} The signal endpoints answer 503 themselves only when `
        + `migration ${SIGNALS_MIGRATION} (or, for the handling filter, ${HANDLING_MIGRATION}) is not applied, and `
        + `this answer named neither. ${NOTHING_KNOWN}`,
    }
  }
  switch (errorKind(error)) {
    case 'unauthorized':
      return {
        kind: 'unauthorized',
        title: 'Not signed in (401)',
        message: `The API did not accept this session's credentials. Sign in again, or check the API key. ${NOTHING_KNOWN}`,
      }
    case 'forbidden':
      return {
        kind: 'forbidden',
        title: 'The server refused this caller (403)',
        message: `The signal store is served to any signed-in caller, yet this request was refused. ${NOTHING_KNOWN}`,
      }
    case 'network':
      return {
        kind: 'network',
        title: 'Could not reach the API',
        message: `The request never got an answer: the API may not be running, or the network refused it. ${NOTHING_KNOWN}`,
      }
    case 'server':
      return {
        kind: 'server',
        title: `The API failed (${status ?? 500})`,
        message: `${errorSummary(error) ?? 'The server gave no reason.'} ${NOTHING_KNOWN}`,
      }
    default:
      return { kind: 'other', title: 'Could not read signals', message: `${errorSummary(error) ?? 'The request failed.'} ${NOTHING_KNOWN}` }
  }
}

/**
 * Retry a signal read only when asking again could change the answer. A 400, 401, 403 and 404 will not, a 503
 * here means a table is not provisioned — which no retry fixes and which only delays the state the reader should
 * see — and a bearer-only read that was not sent (no Entra token) will not be sent the next time either.
 */
export function shouldRetrySignalRequest(failureCount: number, error: unknown): boolean {
  if (errorStatus(error) === 503) return false
  const kind = errorKind(error)
  if (
    kind === 'bad-request' || kind === 'unauthorized' || kind === 'forbidden' || kind === 'not-found'
    || kind === 'sign-in-required'
  ) return false
  return failureCount < 3
}

// ---------------------------------------------------------------------------
// The Inbox's URL
// ---------------------------------------------------------------------------

/** The Inbox's filters, as the URL holds them. Everything here has been validated. */
export interface InboxFilters {
  state: SignalListState
  severity: SignalSeverity | null
  /** A rule id the summary SERVED, in its served spelling. */
  ruleId: string | null
  subjectKind: SignalSubjectKind | null
  /** `observation=unconfirmed`: only signals the detector could not confirm. */
  unconfirmedOnly: boolean
  /** `handling=`: who has them. Only ever set when the summary says the handling table exists. */
  handling: SignalHandlingFilter | null
  /** 1-based. */
  page: number
  /** `signal={id}`: the drawer's signal. A GUID, because the detail route takes nothing else. */
  signalId: string | null
}

export const DEFAULT_INBOX_FILTERS: InboxFilters = {
  state: 'open', severity: null, ruleId: null, subjectKind: null, unconfirmedOnly: false, handling: null, page: 1,
  signalId: null,
}

/** The Handling chips' words, in chip order. `attention` is "Needs attention": unowned, assigned-not-acknowledged, or changed since. */
export const HANDLING_FILTER_LABEL: Record<SignalHandlingFilter, string> = {
  unowned: 'Unowned',
  assigned: 'Assigned',
  acknowledged: 'Acknowledged',
  mine: 'Mine',
  attention: 'Needs attention',
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const MAX_PAGE = 100_000

function pick<T extends string>(list: readonly T[], raw: string | null): T | null {
  if (raw == null) return null
  const wanted = raw.trim().toLowerCase()
  return list.find((v) => v.toLowerCase() === wanted) ?? null
}

/**
 * The Inbox's filters from its URL: `state`, `severity`, `rule`, `kind`, `observation=unconfirmed`, `handling`,
 * `page` and `signal`. An invalid value is DROPPED — read as that filter's default — and so is never sent: the
 * controller refuses an invalid filter with a 400, and a page that forwarded one would fail on a stale bookmark.
 *
 * A rule is valid only if the summary served it (`knownRuleIds`): the SPA lists no rules of its own. A `handling`
 * value is valid only when `handlingAvailable` — the summary said `handlingProvisioned: true` — because the endpoint
 * answers the filter with a 503 when the handling table is missing, and an older API would silently ignore it and
 * list every signal under a "Mine" chip.
 */
export function inboxQueryFromSearch(
  search: URLSearchParams,
  knownRuleIds: readonly string[],
  handlingAvailable = false,
): InboxFilters {
  const pageRaw = search.get('page')?.trim() ?? ''
  const page = /^\d{1,6}$/.test(pageRaw) ? Number(pageRaw) : 1
  const signal = search.get('signal')?.trim() ?? ''
  return {
    state: pick(SIGNAL_LIST_STATES, search.get('state')) ?? 'open',
    severity: pick(SIGNAL_SEVERITIES, search.get('severity')),
    ruleId: pick(knownRuleIds, search.get('rule')),
    subjectKind: pick(SIGNAL_SUBJECT_KINDS, search.get('kind')),
    unconfirmedOnly: search.get('observation')?.trim().toLowerCase() === 'unconfirmed',
    handling: handlingAvailable ? pick(SIGNAL_HANDLING_FILTERS, search.get('handling')) : null,
    page: page >= 1 && page <= MAX_PAGE ? page : 1,
    signalId: GUID.test(signal) ? signal.toLowerCase() : null,
  }
}

/** The URL for a set of filters. Defaults are left out, so the bare route is the default view. */
export function inboxSearchFromQuery(filters: InboxFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.state !== 'open') params.set('state', filters.state)
  if (filters.severity) params.set('severity', filters.severity)
  if (filters.ruleId) params.set('rule', filters.ruleId)
  if (filters.subjectKind) params.set('kind', filters.subjectKind)
  if (filters.unconfirmedOnly) params.set('observation', 'unconfirmed')
  if (filters.handling) params.set('handling', filters.handling)
  if (filters.page > 1) params.set('page', String(filters.page))
  if (filters.signalId) params.set('signal', filters.signalId)
  return params
}

/** Whether anything narrows the list beyond the default "every open signal". */
export function hasInboxFilters(filters: InboxFilters): boolean {
  return filters.state !== 'open' || filters.severity !== null || filters.ruleId !== null
    || filters.subjectKind !== null || filters.unconfirmedOnly || filters.handling !== null
}

/** What the list endpoint is asked for these filters. The key is this object, so it carries no undefined field. */
export function inboxListQuery(filters: InboxFilters, pageSize: number): SignalListQuery {
  const query: SignalListQuery = { state: filters.state, skip: (filters.page - 1) * pageSize, take: pageSize }
  if (filters.severity) query.severity = filters.severity
  if (filters.ruleId) query.ruleId = filters.ruleId
  if (filters.subjectKind) query.subjectKind = filters.subjectKind
  if (filters.unconfirmedOnly) query.observation = 'unobserved'
  if (filters.handling) query.handling = filters.handling
  return query
}
